// Terminology resolution (Module 02 §2.5) — the reason a KazKabel operator sees
// "Bobbin" where a generic factory sees "Stock unit".
//
// §2.5 defines the layering: tenant override → pack override → base locale
// string. `GET /v1/terminology?locale=` already does that resolution and returns
// the EFFECTIVE label for every one of the 61 canonical keys plus the `source`
// that won, so the client never re-implements the precedence — it just asks.
//
// The only client-side layer is the LAST-RESORT fallback while the feed is in
// flight or has failed: `onboarding.terms.<term_key>` in the app dictionary
// (the "base locale string" of §2.5, present in all four locales for the parity
// gate). That guarantees a raw key like `work_center` can never reach a user.
//
// Screens must render entity nouns through `useTerm()` rather than hardcoding
// them in their own i18n keys; a hardcoded "Work order" is a review-blocking
// defect because it silently overrides the tenant's own vocabulary.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { normalizeLocale, serverTextLocale } from "@assessly/contracts/locales";
import type { components } from "@assessly/api-client";
import { useTerminology, type TerminologyLocale } from "./onboarding-queries";

type S = components["schemas"];

/** One of the 61 canonical term keys (`packages/contracts/terminology.ts`). */
export type TermKey = S["TerminologyKey"];

/** Which layer answered — `base` is computed, never stored (D-032). */
export type TermSource = S["TerminologyEntry"]["source"];

/**
 * Map the active i18next language onto a locale the terminology API accepts:
 * the registry's own reading of the tag (`en-GB` → `en`, `zh-TW` → `zh-Hant`),
 * then the locale server text is written in for it (`serverTextLocale`), so a
 * language whose base strings are still pending reads English rather than a
 * neighbour's script. Unknown languages read `en`, as the i18n runtime does.
 */
export function toTerminologyLocale(language: string | undefined): NonNullable<TerminologyLocale> {
  return serverTextLocale(normalizeLocale(language) ?? "en");
}

/** i18n key holding the base label for a term (the in-flight fallback). */
export function termFallbackKey(termKey: string): string {
  return `onboarding.terms.${termKey}`;
}

export interface Terminology {
  /**
   * The tenant-visible label for a canonical term key, already resolved
   * tenant → pack → base. Falls back to the dictionary's base label while the
   * feed loads or if it fails — never returns a raw key.
   */
  term: (key: TermKey) => string;
  /** Which layer supplied the label, or `undefined` before the feed settles. */
  sourceOf: (key: TermKey) => TermSource | undefined;
  /** The locale the labels were resolved for. */
  locale: NonNullable<TerminologyLocale>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The terminology hook every onboarding screen uses for entity nouns.
 *
 * ```tsx
 * const { term } = useTerm();
 * <th>{term("work_center")}</th>
 * ```
 *
 * One shared query per locale (5-minute staleTime), so calling this in a dozen
 * components costs one request.
 */
export function useTerm(): Terminology {
  const { t, i18n } = useTranslation();
  const locale = toTerminologyLocale(i18n.language);
  const query = useTerminology(locale);

  const byKey = useMemo(() => {
    const map = new Map<string, S["TerminologyEntry"]>();
    for (const entry of query.data?.items ?? []) map.set(entry.term_key, entry);
    return map;
  }, [query.data]);

  return {
    locale,
    isLoading: query.isPending,
    isError: query.isError,
    term: (key: TermKey) => {
      const label = byKey.get(key)?.label;
      if (label !== undefined && label.length > 0) return label;
      // defaultValue keeps an unmapped key readable instead of leaking the key.
      return t(termFallbackKey(key), { defaultValue: key });
    },
    sourceOf: (key: TermKey) => byKey.get(key)?.source,
  };
}
